import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, EMPTY, Observable, catchError, forkJoin, mergeMap, of, switchMap } from 'rxjs';
import { GroupService } from './group.service';
import { SocketService } from './socket.service';

/**
 * One entry in the coding view's surgical-assistant autocomplete.
 *
 * Task A4 of `tasks/theatre-booking-and-procedure-lifecycle.md` §5.3. Until the `Colleague`
 * roster existed this list came ENTIRELY from a scan of the last 1,000 `PatientProcedureCode`
 * rows, de-duplicated on decrypted values in memory because the columns use a random IV and
 * `GROUP BY` is therefore impossible. That scan is now the FALLBACK; `Colleagues` where
 * `isSurgicalAssistant = 1` is primary.
 *
 * `source` is what makes the difference visible, and it matters for one concrete reason: only a
 * roster entry can carry a `practiceNumber` the practice has curated. A `'history'` entry
 * carries whatever was typed on a billing line months ago, which is frequently blank — and an
 * assistant with no practice number cannot complete a claim.
 *
 * The server returns roster entries FIRST. Consumers that render the array in order therefore
 * get the right precedence for free; `source` is on the wire so a consumer can also group or
 * label rather than relying on an ordering convention it could silently lose.
 */
export interface AssistantOption {
  name: string;
  practiceNumber: string;
  /** `'roster'` — a curated `Colleague`; `'history'` — a name scanned off past billing lines. */
  source?: 'roster' | 'history';
  /** The `Colleagues.id` behind a roster entry. Null for a historical name. */
  colleagueId?: string | null;
}

/**
 * The range boundaries the billing endpoints expect, for the CALENDAR DAY the user sees.
 *
 * Codes store their date as UTC midnight of the calendar day ('2026-08-22T00:00:00.000Z' is
 * 22 August). The range chips and the date pickers produce LOCAL moments, and
 * `moment.utc(localMidnight)` lands on the previous UTC day in South Africa (UTC+2): picking
 * 23/08 sent 22/08, so the list, the export AND the switching submission all took in one day
 * before the displayed start date (found 2026-09-23). Formatting a moment gives the day in its
 * own mode, local or UTC, which is the day the picker shows. That day is then taken as UTC.
 */
function billingCalendarDay(value: moment.MomentInput): string {
  return (moment.isMoment(value) ? value : moment(value)).format('YYYY-MM-DD');
}

export function billingRangeStartIso(value: moment.MomentInput): string {
  return moment.utc(billingCalendarDay(value)).startOf('day').toISOString();
}

export function billingRangeEndIso(value: moment.MomentInput): string {
  return moment.utc(billingCalendarDay(value)).endOf('day').toISOString();
}

@Injectable({
  providedIn: 'root'
})
export class BillingService {
  private apiUrl = 'api';

  private groupBillingSubject = new BehaviorSubject<any[]>([]);
  groupBilling$ = this.groupBillingSubject.asObservable();

  private billingAlertsSubject = new BehaviorSubject<any[]>([]);
  billingAlerts$ = this.billingAlertsSubject.asObservable();

  groupId: any;
  pageSize = 500;

  /** Incremented at the start of each load; used to ignore stale merge results when the user changes date range. */
  private billingLoadGeneration = 0;
  currentPage = 1;
  isLoading = false;

  alertsCurrentPage = 1;
  alertsIsLoading = false;

  dateRangeOptions: any = {};

  constructor(private http: HttpClient, private groupService: GroupService, private socketService: SocketService) {

    this.setupRealTimeUpdates();

    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          this.resetLoadParameters(selectedGroup);
          return forkJoin([
            this.loadGroupBilling(this.dateRangeOptions.startDate, this.dateRangeOptions.endDate),
            //this.loadBillingAlerts()
          ]);
        }
        return EMPTY;
      })
    ).subscribe();
  }

  private setupRealTimeUpdates(): void {

    // Single code events (legacy support)
    this.socketService.listen('billingAdded', 'billing-service').subscribe((newData) => {
      const current = this.groupBillingSubject.getValue();
      // Add new code at the beginning since it has the most recent createdAt
      this.groupBillingSubject.next([newData, ...current]);
    });

    this.socketService.listen('billingUpdated', 'billing-service').subscribe((updated) => {
      const current = this.groupBillingSubject.getValue();
      const index = current.findIndex(p => p.id === updated.id);
      if (index !== -1) {
        current[index] = updated;
        this.groupBillingSubject.next([...current]);
      }
    });

    this.socketService.listen('billingDeleted', 'billing-service').subscribe((deleted) => {
      const current = this.groupBillingSubject.getValue();
      const updated = current.filter(p => p.id !== deleted.id);
      this.groupBillingSubject.next(updated);
    });

    // Batch events (preferred for performance)
    this.socketService.listen('billingBatchAdded', 'billing-service').subscribe((batchData) => {
      const current = this.groupBillingSubject.getValue();
      // Add new codes at the beginning since they have the most recent createdAt
      this.groupBillingSubject.next([...batchData.codes, ...current]);
    });

    this.socketService.listen('billingBatchUpdated', 'billing-service').subscribe((batchData) => {
      const current = this.groupBillingSubject.getValue();
      const updated = [...current];
      
      // Update each code in the batch
      batchData.codes.forEach((updatedCode: any) => {
        const index = updated.findIndex(p => p.id === updatedCode.id);
        if (index !== -1) {
          updated[index] = updatedCode;
        }
      });
      
      this.groupBillingSubject.next(updated);
    });

    this.socketService.listen('billingBatchDeleted', 'billing-service').subscribe((batchData) => {
      const current = this.groupBillingSubject.getValue();
      // Remove all deleted codes from the current list
      const deletedIds = batchData.codes.map((code: any) => code.id);
      const updated = current.filter(p => !deletedIds.includes(p.id));
      this.groupBillingSubject.next(updated);
    });

    this.socketService.listen('patientUpdated', 'billing-service').subscribe((updatedPatient) => {
      // Get the current value of groupBillingSubject
      const current = this.groupBillingSubject.getValue();

      // Map through the current groupBilling list to update the specific patient's diagnosis
      const updatedBilling = current.map(item => {
        if (item.patientId === updatedPatient.id) {
          // Update the diagnosis for the specific patient
          return {
            ...item,
            Patient: {
              ...item.Patient,
              name: updatedPatient.name,
              surname: updatedPatient.surname,
              medicalAidNumber: updatedPatient.medicalAidNumber,
              medicalDependantCode: updatedPatient.medicalDependantCode,
              diagnosis: updatedPatient.diagnosis,
              patientNumber: updatedPatient.patientNumber,
              medicalAid: updatedPatient.medicalAid,
              medicalAidPlan: updatedPatient.medicalAidPlan,
              gapCover: updatedPatient.gapCover,
              gapCoverNumber: updatedPatient.gapCoverNumber,
              requiresValidation: updatedPatient.requiresValidation,
              requiresICDValidation: updatedPatient.requiresICDValidation,
              wasSelfBoarded: updatedPatient.wasSelfBoarded,
              status: updatedPatient.status,
            }
          };
        }
        return item;
      });

      // Update the groupBillingSubject with the new list
      this.groupBillingSubject.next(updatedBilling);
    });
  }

  private setDateRangeOptions(selectedGroup: any) {
    const { lastBillingExportCreatedDate, lastBillingExportStartDate, lastBillingExportEndDate } = selectedGroup;
    // Same default as the coding view's 'week' filter (billing-view.component.ts
    // setExportOptions). It used to run from the last export to yesterday, which for a group
    // that hadn't exported in months loaded every page of up to 16 months of codes on each
    // visit (tasks/azure-sql-cpu.md §3).
    let startDate = moment().subtract(7, 'days');
    let endDate = moment();

    // Ensure startDate is not greater than endDate
    while (startDate.isAfter(endDate)) {
      startDate = endDate.clone().subtract(1, 'day');
    }

    // Ensure dates are not in the future
    const today = moment();
    if (startDate.isAfter(today)) {
      startDate = today;
    }
    if (endDate.isAfter(today)) {
      endDate = today;
    }

    this.dateRangeOptions = { startDate, endDate, lastBillingExportCreatedDate, lastBillingExportStartDate, lastBillingExportEndDate };
  }

  resetLoadParameters(selectedGroup: any) {
    this.currentPage = 1;
    this.alertsCurrentPage = 1;
    this.groupBillingSubject.next([]);
    this.billingAlertsSubject.next([]);
    this.setDateRangeOptions(selectedGroup);
  }

  loadGroupBilling(startDate: moment.Moment, endDate: moment.Moment, statusFilter?: string, generation?: number): Observable<any[]> {
    // A new first-page load (a new date range or filter, after resetLoadParameters) SUPERSEDES
    // a load still in flight instead of being dropped. It used to return EMPTY while any load
    // was running, so picking a range before the default one had finished sent no request:
    // the list had just been cleared and stayed empty. The superseded load's responses are
    // discarded by the generation check below and leave `isLoading` alone, since it now
    // belongs to the new load.
    const isNewLoad = generation === undefined && this.currentPage === 1;
    if (this.isLoading && !isNewLoad) {
      return EMPTY;
    }
    this.isLoading = true;
    const myGeneration = generation ?? (isNewLoad ? ++this.billingLoadGeneration : this.billingLoadGeneration);
    return this.getBillingByGroup(this.groupId, this.currentPage, this.pageSize, startDate, endDate, statusFilter).pipe(
      mergeMap((res: any) => {
        if (myGeneration !== this.billingLoadGeneration) {
          return of(this.groupBillingSubject.getValue());
        }
        const items = res.data;
        const current = this.groupBillingSubject.getValue();
        const updated = [...current, ...items];
        this.groupBillingSubject.next(updated);
        if (items.length === this.pageSize) {
          this.currentPage++;
          this.isLoading = false;
          return this.loadGroupBilling(startDate, endDate, statusFilter, myGeneration);
        } else {
          this.isLoading = false;
          return of(updated);
        }
      }),
      catchError(error => {
        if (myGeneration === this.billingLoadGeneration) {
          this.isLoading = false;
        }
        return of([]);
      })
    );
  }

  loadBillingAlerts(): Observable<any[]> {
    if (this.alertsIsLoading) {
      return EMPTY;
    }
    this.alertsIsLoading = true;
    return this.getBillingAlertsByGroup(this.groupId, this.alertsCurrentPage, this.pageSize).pipe(
      mergeMap((res: any) => {
        const items = res.data;
        const current = this.billingAlertsSubject.getValue();
        const updated = [...current, ...items];
        this.billingAlertsSubject.next(updated);
        if (items.length === this.pageSize) {
          this.alertsCurrentPage++;
          this.alertsIsLoading = false;
          return this.loadBillingAlerts();
        } else {
          this.alertsIsLoading = false;
          return of(updated);
        }
      }),
      catchError(error => {
        this.alertsIsLoading = false;
        return of([]);
      })
    );
  }

  getBillingByGroup(groupId: string, page: number, pageSize: number, startDate: moment.MomentInput, endDate: moment.MomentInput, statusFilter?: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    startDate = encodeURIComponent(billingRangeStartIso(startDate));
    endDate = encodeURIComponent(billingRangeEndIso(endDate));
    
    let url = `${this.apiUrl}/coding/group/${groupId}?page=${page}&pageSize=${pageSize}&startDate=${startDate}&endDate=${endDate}`;
    if (statusFilter) {
      url += `&statusFilter=${encodeURIComponent(statusFilter)}`;
    }
    
    return this.http.get<any[]>(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getBillingAlertsByGroup(groupId: string, page: number, pageSize: number): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/coding/group/${groupId}/alerts?page=${page}&pageSize=${pageSize}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getAssistantsByGroup(groupId: string): Observable<{ success: boolean; data: AssistantOption[] }> {
    const token = localStorage.getItem('token');
    return this.http.get<{ success: boolean; data: AssistantOption[] }>(
      `${this.apiUrl}/coding/group/${groupId}/assistants`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }

  exportBilling(groupId: string, startDate: moment.MomentInput, endDate: moment.MomentInput, patientIds: string[], statusFilter?: string, format: 'docx' | 'xlsx' = 'docx'): Observable<Blob> {
    const token = localStorage.getItem('token');
    const startDateStr = encodeURIComponent(billingRangeStartIso(startDate));
    const endDateStr = encodeURIComponent(billingRangeEndIso(endDate));
    
    let url = `${this.apiUrl}/coding-export/group/${groupId}/export?startDate=${startDateStr}&endDate=${endDateStr}&format=${format}`;
    if (statusFilter) {
      url += `&statusFilter=${encodeURIComponent(statusFilter)}`;
    }
    
    return this.http.post(url, { patientIds }, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob' // Specify that the response is a Blob
    });
  }

  submitBillingToSwitching(
    groupId: string,
    startDate: moment.MomentInput,
    endDate: moment.MomentInput,
    patientIds: string[],
    statusFilter?: string
  ): Observable<{ success: boolean; submissionId: string; message: string; lastBillingSubmission?: { createdDate: string; startDate: string; endDate: string } }> {
    const token = localStorage.getItem('token');
    const startDateStr = encodeURIComponent(billingRangeStartIso(startDate));
    const endDateStr = encodeURIComponent(billingRangeEndIso(endDate));
    let url = `${this.apiUrl}/coding-submit/group/${groupId}/submit?startDate=${startDateStr}&endDate=${endDateStr}`;
    if (statusFilter) {
      url += `&statusFilter=${encodeURIComponent(statusFilter)}`;
    }
    return this.http.post<{ success: boolean; submissionId: string; message: string; lastBillingSubmission?: { createdDate: string; startDate: string; endDate: string } }>(url, { patientIds }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

}
